import { FC, MouseEventHandler, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { ChevronRight } from '@mui/icons-material';
import styles from './listing-card.module.css';
import { formatRatingValue, roundRatingValue } from '../../../utils/rating';
import { CatalogSuspension } from '../../../apis/search-catalog';

export interface ListingCardProps {
  title: string;
  subtitle: string;
  category?: string;
  rating?: number;
  focused?: boolean;
  isFavourite?: boolean;
  isDigital?: boolean;
  cost?: string;
  type?: 'online' | 'in-person';
  disposition?: 'line' | 'card';
  department?: string;
  onClick: MouseEventHandler;
  onClickFavourite: MouseEventHandler;
  onClickOnlineService: MouseEventHandler;
  href: string;
  suspension?: CatalogSuspension;
}

export const ListingCard: FC<ListingCardProps> = ({
  title,
  subtitle,
  category,
  rating,
  type,
  isDigital,
  cost,
  department,
  onClick,
  onClickFavourite,
  onClickOnlineService,
  href,
  suspension,
}) => {
  const displayRating =
    rating !== undefined ? roundRatingValue(rating) : undefined;

  const isSuspended = useMemo(() => {
    if (!suspension?.enabled) return false;
    const today = new Date().toISOString().split('T')[0];
    if (suspension.startDate > today) return false;
    if (suspension.endDate && suspension.endDate < today) return false;
    return true;
  }, [suspension]);

  return (
    <div data-testid="listing-card" className={styles.card}>
      <div className={styles.category}>{category || 'Categoria'}</div>
      <div className={styles.title}>{title}</div>

      {displayRating !== undefined && (
        <div className={styles.ratingRow}>
          {[1, 2, 3, 4, 5].map((i) => {
            const fraction = Math.min(1, Math.max(0, displayRating - (i - 1)));
            return (
              <span key={i} className={styles.starWrapper}>
                <FontAwesomeIcon icon={faStarRegular} className={styles.starBase} />
                <span
                  className={styles.starFill}
                  style={{ clipPath: `inset(0 ${Math.round((1 - fraction) * 100)}% 0 0)` }}
                >
                  <FontAwesomeIcon icon={faStarSolid} className={styles.starBase} />
                </span>
              </span>
            );
          })}
          <span className={styles.ratingScore}>
            {formatRatingValue(displayRating)}
          </span>
        </div>
      )}

      <div className={styles.footer}>
        <a
          className={styles.moreInfo}
          href={href}
          onClick={(e) => {
            if (
              e.button !== 0 ||
              e.metaKey ||
              e.ctrlKey ||
              e.shiftKey ||
              e.altKey
            ) {
              return;
            }
            e.preventDefault();
            onClick(e);
          }}
          data-testid="listing-card-more-info"
        >
          Mais Informações
          <ChevronRight fontSize="small" />
        </a>

        {isDigital && (
          <button
            id="listing-card-button"
            data-testid="listing-card-button"
            className={styles.button}
            onClick={isSuspended ? undefined : onClickOnlineService}
            disabled={isSuspended}
            aria-disabled={isSuspended}
          >
            Solicitar
            <ChevronRight fontSize="small" />
          </button>
        )}
      </div>
    </div>
  );
};
